import { FC, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'

import { Tooltip } from '@ui/Popups/Tooltip/Tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'

import { PageQuery } from '@utils/ApiClient'
import { formatDate } from '@utils/date.utils'
import { getCurrencySymbol } from '@utils/transaction.utils'

import { LoyaltyRewardsHistoryDto } from '@model/IntroducingBrokerLoyalty'

import styles from './ReportsTable.module.scss'

interface Props {
  data: LoyaltyRewardsHistoryDto[]
  pageQuery?: PageQuery
  setPageQuery?: (value: PageQuery) => void
}

export const ReportsTable: FC<Props> = (props) => {
  const { data } = props
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType?.name
  const { t } = useTranslation()

  const tableHeaderRows = [
    { name: t('Date'), id: '1' },
    { name: t('Event'), id: '2' },
    { name: t('Amount'), id: '3' },
  ]

  const eventMap = {
    created: {
      name: t('IB.Dashboard.Pending'),
      info: t('IB.Checking qualification criteria'),
    },
    approved: {
      name: t('IB.Became available'),
      info: '',
    },
    rejected: {
      name: t('Rejected'),
      info: t('IB.Qualification criteria was not met'),
    },
    redeemed: {
      name: t('Claimed'),
      info: '',
    },
  }

  if (!data.length) {
    return <TableNoResults title={t('No results')} />
  }

  return (
    <Table className={styles.table}>
      <TableHeader>
        <TableRow>
          {tableHeaderRows?.map((option, index) => {
            return <TableHead key={`${option.name}_${index}`}>{option.name}</TableHead>
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((item, index) => {
          const event = eventMap[item.rewardStatus?.name.toLowerCase() as keyof typeof eventMap]
          const eventInfo = event?.name ? `${event.name} - ${item.tier.name}` : ''

          return (
            <TableRow key={`${item.tier.id}_${index}`}>
              <TableCell className={styles.tableCell}>
                {formatDate(item.timestamp, {
                  formatType: dateFormat,
                  withTime: false,
                })}
              </TableCell>
              <TableCell className={styles.tableCell}>
                {' '}
                <Tooltip.New content={event?.info} disabled={!event?.info} direction='top'>
                  {eventInfo}
                </Tooltip.New>
              </TableCell>
              <TableCell className={styles.tableCell}>{`${getCurrencySymbol('USD')}${
                item.amount
              }`}</TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
